import { Card } from '../../components/Card';
import { clsx } from '../../components/clsx';
import { formatShortDay, parseLocalDateKey } from '../../lib/date';
import { quoteScale, type QuoteScale } from '../../lib/quote/dailyQuote';
import { QuoteArtLayers } from './QuoteArt';
import { useQuoteOfTheDay } from './useQuoteOfTheDay';

/**
 * Quote of the Day.
 *
 * The spec calls this card "highly stylized … a designed visual, not a plain
 * text block", so it is the one panel that opts out of the shared card
 * chrome: `padded={false}` + `showHeader={false}` hand the whole rectangle to
 * the composition, and the card renders its own eyebrow over the art.
 *
 * Everything is a function of one day key: the quote, the art, and the image
 * cache. Reload and you get the identical card; cross midnight in a pinned
 * tab and the whole thing re-derives on the shared clock without a refresh.
 *
 * The status machinery in <Card> is deliberately unused. There is no loading,
 * error, empty or unconfigured state to show — the card is complete with zero
 * configuration and zero network, and an image that fails is simply a card
 * without an image.
 */

/** Short chip date. `formatLongDate` is too wide for the 369px card. */
const CHIP_MONTH_DAY = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

function chipLabel(dayKey: string): string {
  const date = parseLocalDateKey(dayKey);
  // A key this component did not build would have to be corrupt to land here;
  // showing the raw key beats showing "Invalid Date".
  return date === null ? dayKey : `${formatShortDay(date)} · ${CHIP_MONTH_DAY.format(date)}`;
}

/**
 * Type size by quote length. The body is clamped to four lines, so without
 * this a long quote would be truncated rather than merely set smaller —
 * the specific way this card fails if nobody thinks about it.
 */
const QUOTE_TYPE: Readonly<Record<QuoteScale, string>> = {
  lg: 'text-[22px] leading-[1.26]',
  md: 'text-[18px] leading-[1.3]',
  sm: 'text-[15px] leading-[1.34]',
};

export function QuotePanel() {
  const { dayKey, quote, art, image, imagePhase, reportImageBroken } = useQuoteOfTheDay();

  return (
    <Card
      title="Quote of the Day"
      icon="💬"
      accent="quote"
      padded={false}
      showHeader={false}
      testId="quote-panel"
    >
      <div className="relative h-full w-full overflow-hidden">
        <QuoteArtLayers art={art} image={image} onImageError={reportImageBroken} />

        {/*
          The ornament. U+201C in the serif face rather than U+275D, which
          falls back to a colour emoji font on some systems and would land as
          a bright blob instead of a faint ink mark.
        */}
        <span
          aria-hidden
          className="pointer-events-none absolute -top-5 left-1 select-none font-serif text-[120px] leading-none text-white/[0.13]"
        >
          &ldquo;
        </span>

        {/* Light type in BOTH themes: the art underneath is a mid-dark field
            in both, by construction (see gradient.ts), so one contrast model
            covers the whole card. */}
        <div className="relative flex h-full min-h-0 flex-col justify-between p-4 text-white">
          <div className="flex shrink-0 items-start gap-2">
            <p className="min-w-0 truncate text-[9.5px] font-semibold uppercase tracking-[0.16em] text-white/75">
              <span aria-hidden className="mr-1.5">
                💬
              </span>
              Quote of the Day
            </p>

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {imagePhase === 'generating' && (
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/80">
                  Painting…
                </span>
              )}
              <span
                className="rounded-full bg-white/15 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-white/85"
                title={dayKey}
              >
                {chipLabel(dayKey)}
              </span>
            </div>
          </div>

          <figure className="mt-3 min-h-0 shrink">
            <blockquote
              data-testid="quote-text"
              className={clsx(
                'line-clamp-4 font-serif font-medium text-white',
                // A soft shadow rather than a heavier scrim: it buys legibility
                // over a generated photograph without dulling the gradient.
                '[text-shadow:0_1px_12px_rgba(0,0,0,0.45)]',
                QUOTE_TYPE[quoteScale(quote.text)],
              )}
            >
              {quote.text}
            </blockquote>

            <figcaption className="mt-3 flex items-center gap-2">
              <span aria-hidden className="h-px w-6 shrink-0 bg-white/55" />
              <cite
                data-testid="quote-author"
                className="min-w-0 truncate text-[10px] font-semibold uppercase not-italic tracking-[0.18em] text-white/85"
              >
                {quote.author}
              </cite>
            </figcaption>
          </figure>
        </div>
      </div>
    </Card>
  );
}
